import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ReviewedQuestion } from '@titrate/shared';
import { api, ApiRequestError } from '../lib/api';
import { useReview } from '../lib/queries';
import { ErrorNotice, Markdown, Spinner } from '../components/ui';

export function Review() {
  const { sessionId = '' } = useParams();
  const { data, isLoading, error } = useReview(sessionId);
  const [filter, setFilter] = useState<'all' | 'incorrect'>('incorrect');

  if (isLoading) return <Spinner label="Loading your answers" />;
  if (error || !data) return <ErrorNotice message={error instanceof Error ? error.message : 'Not found.'} />;

  const incorrectCount = data.questions.filter((q) => q.isCorrect === false).length;
  const shown = filter === 'all' ? data.questions : data.questions.filter((q) => q.isCorrect === false);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-muted">{data.examCode}</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-ink">Answer review</h1>
          <p className="mt-2 text-sm text-muted">
            {incorrectCount} of {data.questions.length} answered incorrectly. Working through these is
            where most of the improvement comes from.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={filter === 'incorrect' ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setFilter('incorrect')}
          >
            Incorrect only ({incorrectCount})
          </button>
          <button
            type="button"
            className={filter === 'all' ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setFilter('all')}
          >
            All ({data.questions.length})
          </button>
        </div>
      </header>

      {shown.length === 0 ? (
        <div className="card p-6 text-center sm:p-10">
          <p className="text-base font-semibold text-ink">Nothing incorrect to review.</p>
          <p className="mt-2 text-sm text-muted">
            Switch to &ldquo;All&rdquo; to read the explanations for the questions you got right.
          </p>
        </div>
      ) : null}

      <ol className="space-y-5">
        {shown.map((q) => (
          <li key={q.id}>
            <ReviewCard question={q} />
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap gap-3">
        <Link to="/dashboard" className="btn-secondary">Back to dashboard</Link>
        <Link to="/practice" className="btn-primary">Practise these topics</Link>
      </div>
    </div>
  );
}

function ReviewCard({ question }: { question: ReviewedQuestion }) {
  const [tutorOpen, setTutorOpen] = useState(false);
  const correct = question.isCorrect === true;

  return (
    <article className={`card p-4 sm:p-6 ${correct ? '' : 'border-l-4 border-l-red-400'}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`badge ${correct ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200' : 'bg-red-50 dark:bg-red-500/10 text-red-800 dark:text-red-200'}`}>
          {question.userAnswer === null ? 'Not answered' : correct ? 'Correct' : 'Incorrect'}
        </span>
        <span className="badge bg-raised text-muted">{question.domainCode} · {question.domainName}</span>
        <span className="badge bg-raised text-muted capitalize">{question.difficulty}</span>
        {question.timeTakenSec !== null ? (
          <span className="text-subtle">{question.timeTakenSec}s</span>
        ) : null}
        {question.referencedBook ? <span className="badge bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200">Looked it up</span> : null}
      </div>

      <p className="mt-3.5 whitespace-pre-wrap text-sm leading-relaxed text-ink">
        {question.stem.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
          part.startsWith('**') && part.endsWith('**') ? (
            <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>
          ) : (
            <span key={i}>{part}</span>
          ),
        )}
      </p>

      {question.options ? (
        <ul className="mt-4 space-y-1.5">
          {question.options.map((option, i) => {
            const isAnswer = option === question.correctAnswer;
            const isChosen = option === question.userAnswer;
            return (
              <li
                key={option}
                className={`flex gap-2.5 rounded-lg border p-3 text-sm ${
                  isAnswer
                    ? 'border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10'
                    : isChosen
                      ? 'border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10'
                      : 'border-line'
                }`}
              >
                <span className="font-medium text-muted">{String.fromCharCode(65 + i)}.</span>
                <span className="flex-1 text-ink">{option}</span>
                {isAnswer ? <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">Correct</span> : null}
                {isChosen && !isAnswer ? <span className="text-xs font-semibold text-red-700 dark:text-red-300">Your answer</span> : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-lg border border-line p-3">
            <dt className="text-xs text-muted">Your answer</dt>
            <dd className="mt-0.5 font-mono font-semibold text-ink">
              {question.userAnswer ?? '—'} {question.unit}
            </dd>
          </div>
          <div className="rounded-lg border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 p-3">
            <dt className="text-xs text-emerald-700 dark:text-emerald-300">Correct answer</dt>
            <dd className="mt-0.5 font-mono font-semibold text-emerald-900 dark:text-emerald-200">
              {question.correctAnswer} {question.unit}
            </dd>
          </div>
        </dl>
      )}

      <div className="mt-4 rounded-lg bg-raised p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Explanation</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-muted">{question.explanation}</p>
      </div>

      <div className="mt-4">
        <button type="button" className="btn-secondary" onClick={() => setTutorOpen((o) => !o)}>
          {tutorOpen ? 'Hide AI tutor' : 'Ask the AI tutor about this'}
        </button>
      </div>
      {tutorOpen ? <TutorPanel questionId={question.id} /> : null}
    </article>
  );
}

interface TutorTurn {
  role: 'user' | 'assistant';
  content: string;
}

function TutorPanel({ questionId }: { questionId: string }) {
  const [turns, setTurns] = useState<TutorTurn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaHit, setQuotaHit] = useState(false);

  const send = async () => {
    const message = input.trim();
    if (!message) return;
    setTurns((t) => [...t, { role: 'user', content: message }]);
    setInput('');
    setBusy(true);
    setError(null);
    setQuotaHit(false);
    try {
      const { reply } = await api.post<{ reply: string }>('/api/tutor/message', { questionId, message });
      setTurns((t) => [...t, { role: 'assistant', content: reply }]);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
        setQuotaHit(err.isQuotaError);
      } else {
        setError('The AI tutor could not answer just now.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-line bg-surface p-4">
      <div className="space-y-3">
        {turns.map((t, i) => (
          <div key={i} className={t.role === 'user' ? 'text-right' : ''}>
            <div
              className={`inline-block max-w-[85%] rounded-lg px-3.5 py-2.5 text-left text-sm ${
                t.role === 'user' ? 'bg-brand-700 text-white' : 'bg-raised text-ink'
              }`}
            >
              {t.role === 'assistant' ? <Markdown content={t.content} /> : t.content}
            </div>
          </div>
        ))}
        {busy ? <p className="text-sm text-muted">Thinking…</p> : null}
      </div>

      {error ? (
        <div className="mt-3">
          <ErrorNotice title={quotaHit ? 'AI tutor limit reached' : 'AI tutor unavailable'} message={error} />
          {quotaHit ? <Link to="/pricing" className="btn-primary mt-3">See plans</Link> : null}
        </div>
      ) : null}

      <div className="mt-3 flex gap-2">
        <input
          className="input flex-1"
          placeholder="Why is that option wrong?"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !busy) void send();
          }}
        />
        <button type="button" className="btn-primary" onClick={() => void send()} disabled={busy || !input.trim()}>
          Ask
        </button>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-muted">
        Answers are AI-generated, not written by a pharmacist. Check anything clinically important
        against the AMH, APF or Therapeutic Guidelines. Each message uses one of your monthly tutor
        credits.
      </p>
    </div>
  );
}
